import { HStack, VStack } from '@cbhq/cds-web/layout';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { memo, useCallback, useMemo, useState } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@cbhq/cds-web/tables';
import { TextHeadline } from '@cbhq/cds-web/typography';
import { SearchInput, SelectOption } from '@cbhq/cds-web/controls';
import { SelectChip } from '@cbhq/cds-web/chips';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { ExploreListDesktopRow } from '@/components/Explore/ExploreListDesktopRow';
import { ExploreListMobileRow } from '@/components/Explore/ExploreListMobileRow';
import { useExploreData, ExplorePoolData } from '@/components/Explore/hooks/useExploreData';

type SortDirection = 'asc' | 'desc' | null;

// Columns for the Explore table (matching Figma design)
// Widths based on Figma: Pool=314px, TVL=211px, APR=120px, Volume=177px, Fee=161px (total=983px)
const ExploreListColumns = [
  { label: 'Pool', field: 'name', width: '32%' },
  { label: 'TVL', tip: 'Total Value Locked in USD', field: 'tvlNotional', width: '21%' },
  { label: 'APR', tip: 'Annual Percentage Rate', field: 'aprPercentage', width: '12%' },
  {
    label: 'Volume (24h)',
    tip: '24 hour trading volume in USD',
    field: 'volume24hNotional',
    width: '18%',
  },
  {
    label: 'Fee (24h)',
    tip: '24 hour fees generated in USD',
    field: 'fees24hNotional',
    width: '17%',
  },
];

type ExploreListItemsProps = {
  search: string;
  sortField: string | null;
  sortDirection: SortDirection;
  isMobile: boolean;
  onPoolSelect?: (poolId: string) => void;
};

function ExploreListItems({
  search,
  sortField,
  sortDirection,
  isMobile,
  onPoolSelect,
}: ExploreListItemsProps) {
  const { pools } = useExploreData();

  const poolsList = useMemo(() => {
    // First filter by search
    let filteredPools = pools.filter((pool) =>
      pool.name.toLowerCase().includes(search.toLowerCase()),
    );

    // Then sort if needed
    if (sortField && sortDirection) {
      filteredPools = [...filteredPools].sort((a, b) => {
        // Handle string fields (like name) separately
        if (sortField === 'name') {
          return sortDirection === 'asc'
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        }

        // For numeric fields
        const aValue = a[sortField as keyof ExplorePoolData] as number;
        const bValue = b[sortField as keyof ExplorePoolData] as number;

        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      });
    }

    return filteredPools;
  }, [pools, search, sortField, sortDirection]);

  return (
    <>
      {poolsList.map((pool) =>
        isMobile ? (
          <ExploreListMobileRow key={pool.poolId} pool={pool} onSelect={onPoolSelect} />
        ) : (
          <ExploreListDesktopRow key={pool.poolId} pool={pool} onSelect={onPoolSelect} />
        ),
      )}
    </>
  );
}

type ExploreListContentProps = {
  onPoolSelect?: (poolId: string) => void;
};

const ExploreListContent = memo(function ExploreListContent({
  onPoolSelect,
}: ExploreListContentProps) {
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const { isPhone, isPhoneLandscape, isTablet } = useBreakpoints();
  const isMobile = isPhone || isPhoneLandscape || isTablet;

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        // Cycle through: asc -> desc -> null
        const newDirection =
          sortDirection === 'asc' ? 'desc' : sortDirection === 'desc' ? null : 'asc';
        setSortDirection(newDirection);
        if (newDirection === null) {
          setSortField(null);
        }
      } else {
        // Start with ascending for a new field
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection],
  );

  return (
    <>
      {/* Filter and Search Section */}
      <VStack paddingY={2} paddingX={3}>
        <HStack alignItems="center" justifyContent="space-between">
          {/* Filter dropdown */}
          <SelectChip
            value="All"
            content={<SelectOption value="All" title="All" />}
            background="bgAlternate"
            height={42}
          >
            All
          </SelectChip>

          {/* Search input */}
          <SearchInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search"
            compact
            width={isMobile ? 220 : 240}
          />
        </HStack>
      </VStack>

      {/* Table */}
      <VStack maxWidth="100%">
        <Table>
          {!isMobile && (
            <TableHeader>
              <TableRow>
                {ExploreListColumns.map(({ label, field, width }) => {
                  const isSorted = sortField === field;
                  const iconName = !isSorted
                    ? undefined
                    : sortDirection === 'asc'
                    ? 'sortUp'
                    : 'sortDown';

                  const headerContent = (
                    <HStack alignItems="center" gap={0.5}>
                      <TextHeadline as="span" noWrap color="fgMuted">
                        {label}
                      </TextHeadline>
                      {iconName && <Icon name={iconName} size="xs" color="fg" />}
                    </HStack>
                  );

                  return (
                    <TableCell
                      key={label}
                      width={width}
                      alignItems="flex-start"
                      onClick={field !== 'name' ? () => handleSort(field) : undefined}
                      testID={`sort-column-${field}`}
                    >
                      {headerContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHeader>
          )}
          <TableBody>
            <ExploreListItems
              search={search}
              sortField={sortField}
              sortDirection={sortDirection}
              isMobile={isMobile}
              onPoolSelect={onPoolSelect}
            />
          </TableBody>
        </Table>
      </VStack>
    </>
  );
});

type ExploreListProps = {
  onPoolSelect?: (poolId: string) => void;
};

export const ExploreList = memo(function ExploreList({ onPoolSelect }: ExploreListProps) {
  return (
    <VStack background="bg" borderRadius={300}>
      <ExploreListContent onPoolSelect={onPoolSelect} />
    </VStack>
  );
});
